import { useTranslation } from 'react-i18next';

import { AccountLayout } from '@components/layout/AccountLayout';

const GHOST_INVOICES = [
  { numW: 'w-16', clientW: 'w-28', dateW: 'w-20', amountW: 'w-14', status: 'paid' },
  { numW: 'w-16', clientW: 'w-36', dateW: 'w-20', amountW: 'w-16', status: 'pending' },
  { numW: 'w-16', clientW: 'w-24', dateW: 'w-20', amountW: 'w-12', status: 'paid' },
  { numW: 'w-16', clientW: 'w-32', dateW: 'w-20', amountW: 'w-16', status: 'overdue' },
  { numW: 'w-16', clientW: 'w-20', dateW: 'w-20', amountW: 'w-14', status: 'pending' },
];

const STATUS_COLORS: Record<string, string> = {
  paid:    'bg-green-200/60',
  pending: 'bg-amber-200/60',
  overdue: 'bg-red-200/60',
};

function SummaryTiles() {
  return (
    <div className="grid grid-cols-3 gap-3">
      {['w-20', 'w-16', 'w-24'].map((w, i) => (
        <div key={i} className="rounded-2xl border border-border bg-white/60 p-5">
          <div className={`mb-3 h-2 rounded-full bg-muted/55 ${w}`} />
          <div className="mb-1.5 h-7 w-20 rounded-lg bg-muted/45" />
          <div className="h-2 w-12 rounded-full bg-muted/35" />
        </div>
      ))}
    </div>
  );
}

function FilterPills() {
  const widths = ['w-8', 'w-10', 'w-14', 'w-12'];
  return (
    <div className="flex items-center gap-2">
      {widths.map((w, i) => (
        <div
          key={i}
          className={`h-7 rounded-full ${i === 0 ? 'bg-primary/25' : 'bg-muted/30'} ${w}`}
        />
      ))}
      <div className="ml-auto h-9 w-28 rounded-xl bg-primary/20" />
    </div>
  );
}

function InvoiceTableGhost() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-white/60">
      <div className="grid grid-cols-[1fr_2fr_1.5fr_1fr_1fr] gap-4 border-b border-border px-5 py-3">
        {['w-8', 'w-12', 'w-10', 'w-14', 'w-10'].map((w, i) => (
          <div key={i} className={`h-2 rounded-full bg-muted/55 ${w}`} />
        ))}
      </div>

      {GHOST_INVOICES.map((row, i) => (
        <div
          key={i}
          className="grid grid-cols-[1fr_2fr_1.5fr_1fr_1fr] items-center gap-4 border-b border-border/50 px-5 py-3.5 last:border-b-0"
        >
          <div className={`h-2.5 rounded-full bg-muted/50 ${row.numW}`} />
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 shrink-0 rounded-full bg-primary/20" />
            <div className={`h-2.5 rounded-full bg-muted/55 ${row.clientW}`} />
          </div>
          <div className={`h-2 rounded-full bg-muted/40 ${row.dateW}`} />
          <div className={`h-2.5 rounded-full bg-muted/50 ${row.amountW}`} />
          <div className={`h-5 w-14 rounded-full ${STATUS_COLORS[row.status]}`} />
        </div>
      ))}
    </div>
  );
}

export function InvoicesPage() {
  const { t } = useTranslation();

  return (
    <AccountLayout>
      <div
        className="relative min-h-[calc(100vh-4rem)] overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse 75% 55% at 65% -5%, hsl(160 51% 24% / 0.08) 0%, transparent 70%), radial-gradient(ellipse 55% 45% at -5% 88%, hsl(160 51% 24% / 0.06) 0%, transparent 65%), hsl(var(--color-background))',
        }}
      >
        <div className="pointer-events-none absolute -right-24 -top-24 text-primary" aria-hidden="true">
          <svg width="480" height="480" viewBox="0 0 480 480" fill="none">
            <circle cx="240" cy="240" r="200" stroke="currentColor" strokeWidth="1"   opacity="0.08" />
            <circle cx="240" cy="240" r="150" stroke="currentColor" strokeWidth="1"   opacity="0.07" />
            <circle cx="240" cy="240" r="100" stroke="currentColor" strokeWidth="1.5" opacity="0.06" />
            <circle cx="240" cy="240" r="50"  stroke="currentColor" strokeWidth="2"   opacity="0.05" />
          </svg>
        </div>

        <div className="pointer-events-none absolute -bottom-40 -left-40 text-primary" aria-hidden="true">
          <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
            <circle cx="200" cy="200" r="160" stroke="currentColor" strokeWidth="1"   opacity="0.06" />
            <circle cx="200" cy="200" r="110" stroke="currentColor" strokeWidth="1"   opacity="0.05" />
            <circle cx="200" cy="200" r="60"  stroke="currentColor" strokeWidth="1.5" opacity="0.04" />
          </svg>
        </div>

        <div className="relative mx-auto flex max-w-3xl flex-col items-center px-4 pb-20 pt-16 sm:pt-20">

          <div className="mb-6 flex h-[72px] w-[72px] items-center justify-center rounded-2xl bg-primary shadow-md">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>

          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">
              {t('layout.account.comingSoon')}
            </span>
          </div>

          <h1 className="mb-3 font-display text-5xl font-semibold text-foreground sm:text-6xl">
            {t('layout.account.nav.invoices')}
          </h1>

          <p className="mb-14 max-w-xs text-center text-base leading-relaxed text-muted-foreground">
            Create, send, and track invoices — with a clear view of what's paid and what's pending.
          </p>

          <div className="w-full space-y-3 opacity-40">
            <SummaryTiles />
            <FilterPills />
            <InvoiceTableGhost />
          </div>

        </div>
      </div>
    </AccountLayout>
  );
}
